import PropTypes from 'prop-types';
import React from 'react';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { ThemeProvider } from 'styled-components';

import GridList from 'ravenjs/lib/GridList';
import GridListItem from 'ravenjs/lib/GridListItem';
import { genID } from 'ravenjs/utils/generate';
import { getMergedThemeFromSchema } from 'ravenjs/utils/theme';
import { warning } from 'ravenjs/utils/warning';

import SampleDataGridNoData from './SampleDataGridNoData';
import SampleDataGridItem from './SampleDataGridItem';
import DataGridStyled from './DataGridStyled';

// eslint-disable-next-line valid-jsdoc
/**
 * A simple data-grid component.
 */
function DataGrid(props) {
    const { data, schema, style } = props;
    const gridProps = _get(schema, 'grid', {});
    const itemProps = _get(schema, 'item', {});
    // Extract the `Item` Component,
    // this is what is rendered within each of the `GridListItem`.
    const ItemComponent = _get(schema, 'ItemComponent') || SampleDataGridItem;
    // This is a helpful schema property, `NoDataComponent`,
    // it helps displaying a No Data Available component when there is no data.
    const NoDataComponent = _get(schema, 'NoDataComponent') || SampleDataGridNoData;
    // Throw a warning if no `schema` is provided.
    warning(!_isEmpty(schema), [`A <DataGrid /> requires a 'schema' object to build the grid.`]);
    // Render `null` if no `schema` is provided.
    if (_isEmpty(schema)) {
        return null;
    }
    // Display a component if no data is available.
    if (!data || data.length === 0) {
        return (
            <ThemeProvider theme={getMergedThemeFromSchema(props)}>
                <DataGridStyled style={style}>
                    <NoDataComponent schema={schema} />
                </DataGridStyled>
            </ThemeProvider>
        );
    }
    // Render the `GridList` if alls good.
    return (
        <ThemeProvider theme={getMergedThemeFromSchema(props)}>
            <DataGridStyled style={style}>
                <GridList {...gridProps}>
                    {data.map(item => (
                        <GridListItem key={genID('DataGrid__Item')} {...itemProps}>
                            <ItemComponent
                                gridProps={gridProps}
                                item={item}
                                itemProps={itemProps}
                            />
                        </GridListItem>
                    ))}
                </GridList>
            </DataGridStyled>
        </ThemeProvider>
    );
}

DataGrid.propTypes = {
    /**
     * The main data for the body of the component.
     */
    data: PropTypes.array,
    /**
     * The JSON schema for the `DataGrid`.
     */
    schema: PropTypes.object,
    /**
     * The css styles for the component.
     */
    style: PropTypes.object,
};

DataGrid.defaultProps = {
    data: [],
    schema: {},
    style: {},
};

export default DataGrid;
